"use client";

import { useEffect } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log richer diagnostics than the default `console.error(error)` —
    // an empty thrown `{}` would otherwise log as `Object {}` with no
    // detail. Surfaces name, message, stack, and own properties.
    try {
      const detail = {
        name: (error as { name?: string })?.name ?? null,
        message: (error as { message?: string })?.message ?? null,
        stack: (error as { stack?: string })?.stack ?? null,
        digest: (error as { digest?: string })?.digest ?? null,
        ownKeys:
          error && typeof error === "object"
            ? Object.getOwnPropertyNames(error)
            : [],
        stringified: (() => {
          try {
            return JSON.stringify(
              error,
              Object.getOwnPropertyNames(error ?? {})
            );
          } catch {
            return "<unserializable>";
          }
        })(),
      };
      console.error("[GlobalError]", detail);
    } catch {
      console.error("[GlobalError fallback]", error);
    }
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="max-w-md text-center">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Something went wrong
        </p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight">
          A small mishap.
        </h1>
        <p className="mt-3 text-muted-foreground">
          We&apos;ve logged the error. Try again, or head back home — usually
          one of the two does the trick.
        </p>
        {error.digest && (
          <p className="mt-4 font-mono text-xs text-muted-foreground/70">
            ref: {error.digest}
          </p>
        )}
        <div className="mt-8 flex items-center justify-center gap-3">
          <Button onClick={reset} variant="default" className="rounded-full">
            Try again
          </Button>
          <Link
            href="/"
            className="text-sm underline underline-offset-4 hover:text-foreground"
          >
            Home
          </Link>
        </div>
      </div>
    </div>
  );
}
